import './myBooking.css';

import { useEffect, useState } from 'react';

import EmptyPage from '../../components/empty-page/emptyPage';
import ReactLoading from 'react-loading';
import TitleBar from '../../components/titleBar/titleBar';
import CancelBooking from '../../components/tripStrap/tripStrapFun/cancelBooking';
import { handleBooking } from '../../components/tripStrap/tripStrapFun/handleBooking';
import myBookingData, { myBookingCount } from './myBookingFun/myBookingData';
import Pagination from '../../components/pagination/Pagination';
import { clearBookingStorage } from '../../utils/clearStorage';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TripStrapNew from '../../components/tripStrapNew/TripStrapNew';
import { getFormattedDateReverse, isTripDateMoreThanNumOfDays } from '../../utils/date';
import { userReview } from '../../api/userAPI';
import CloseIcon from '../../assets/icons/trvto_close-rounded.svg';
import { toast } from 'react-toastify';
import RateTrip from '../../components/ratingTrip/rateTrip';
import TripStrapMobile from '../../components/tripStrapMobile/TripStrapMobile';
import { getRefundAmount } from '../../components/tripStrap/amountRefundFun/amountRefund';
import { getTotalSeatCount } from '../../utils/getTotalSeatCount';
import TripStrap from '../../components/tripStrap/TripStrap';
import RescheduleBooking from '../../components/tripStrap/tripStrapFun/RescheduleBooking';
import { getTypeShort } from '../../utils/getType';
import StayRescheduleModal from '../../components/tripStrap/tripStrapFun/StayRescheduleModal';

export const getBookingDetailsByType = (type) => {
    const config = {
        trip: {
            label: 'Trip Date'
        },
        guide: {
            label: 'Guide Date'
        },
        activity: {
            label: 'Activity Date'
        },
        camp: {
            label: 'Check-in'
        }
    };

    return (
        config[type] || {
            label: 'Date'
        }
    );
};

const RATE_TITLE = {
    trip: 'Rate Your Previous Trip',
    camp: 'Rate Your Stay',
    act: 'Rate Your Activity',
    gu: 'Rate Your Tour'
};

/*
 * Which buttons a booking gets (same rules as the new My Bookings):
 *   type: trip | camp | act | gu
 */
export function bookingActions(item, type) {
    const upcoming = item?.status === 'Not Yet Started';
    const started = item?.startDate && new Date(item.startDate) <= new Date();
    // stays booked with the stay directly (support "Add booking") are changed by them
    const manual = item?.bookedVia === 'Manual';
    const moreThan = (days) => Boolean(item?.startDate) && isTripDateMoreThanNumOfDays(item.startDate, days);
    return {
        pickup: type === 'trip' && upcoming && moreThan(1),
        meetup: (type === 'act' || type === 'gu') && upcoming && Boolean(item?.meetupLocation?.name) && moreThan(1),
        // stays: date change switched off until its edge cases are handled
        // reschedule: type === 'camp' ? upcoming && !manual && moreThan(7) : upcoming && moreThan(10),
        reschedule: type === 'camp' ? false : upcoming && moreThan(10),
        rate: Boolean(started) && ['Completed', 'In Progress'].includes(item?.status),
        cancel: upcoming && !(type === 'camp' && manual),
        bookAgain: !upcoming
    };
}

export default function MyBooking() {
    const [bookingData, setBookingData] = useState([]);
    const navigate = useNavigate();
    const isMobile = window.innerWidth < 768;
    const [refetch, setRefetch] = useState(false);
    const [loading, setLoading] = useState(false);
    const [totalData, setTotalData] = useState(0);
    const [searchParams, setSearchParams] = useSearchParams();
    const typeSel = searchParams.get('type') || 'trip';
    const limit = Number(searchParams.get('limit')) || 10;
    const pageNumber = Number(searchParams.get('page')) || 1;
    const offSet = (pageNumber - 1) * limit;
    const [detailsData, setDetailsData] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [userRating, setUserRating] = useState(0);
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [comment, setComment] = useState('');
    const [popupRating, setPopupRating] = useState(userRating);
    const [refundAmount, setRefundAmount] = useState(0);
    // the booking a popup (rate / reschedule / cancel) is for — on mobile it's the tapped card
    const [actionItem, setActionItem] = useState(null);
    const types = ['trip', 'camp', 'guide', 'activity'];
    const typeMap = {
        trip: 'trip',
        guide: 'gu',
        activity: 'act',
        camp: 'camp'
    };

    const idType = {
        trip: 'packId',
        guide: 'guTourId',
        activity: 'actId',
        camp: 'campId'
    };

    useEffect(() => {
        clearBookingStorage();
    }, []);

    useEffect(() => {
        myBookingCount(typeMap[typeSel], setTotalData);
    }, [typeSel]);

    useEffect(() => {
        if (!totalData) return;

        const page = Number(searchParams.get('page')) || 1;
        const limitParam = Number(searchParams.get('limit')) || 10;
        const maxPage = Math.ceil(totalData / limitParam);

        if (page > maxPage) {
            setSearchParams({
                page: 1,
                limit: limitParam
            });
        }
    }, [totalData]);

    useEffect(() => {
        const fetchBookingData = async () => {
            try {
                await myBookingData(typeMap[typeSel], limit, offSet, setBookingData, setLoading);
            } catch (err) {
                console.log('Failed to fetch search results');
            }
        };

        fetchBookingData();
    }, [typeSel, refetch, offSet]);

    useEffect(() => {
        setDetailsData({});
        setSelectedId(null);
    }, [typeSel]);

    useEffect(() => {
        if (bookingData?.length > 0) {
            setDetailsData(bookingData[0]);
            setSelectedId(bookingData[0]?._id || '');
        }
    }, [bookingData]);

    const typeShort = typeMap[typeSel];
    const can = bookingActions(detailsData, typeShort);

    const openRatingModal = (e, item = detailsData) => {
        e.stopPropagation();
        setActionItem(item);
        setShowRatingModal(true);
        setPopupRating(0);
        setComment('');
    };

    const handleRescheduleBooking = (event, item = detailsData) => {
        event.stopPropagation();
        setActionItem(item);
        setShowRescheduleModal(true);
    };

    // pickup (trip) / meetup (activity, guide): picked on the details page, "Update Now"
    const openForChange = (event, item, extra = {}) => {
        event.stopPropagation();
        sessionStorage.setItem('confirmedBookingData', JSON.stringify({ ...item, ...extra }));
        navigate(`/${typeShort}/details/${item?.id?._id}`);
    };

    const closeRatingModal = () => {
        setShowRatingModal(false);
    };

    const paginationClick = (page) => {
        if (Math.ceil(totalData / limit) <= 1) return;
        setSearchParams({
            type: typeSel,
            page,
            limit
        });
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    };

    //eslint-disable-next-line
    const handleCancelBooking = async (e, data) => {
        e.stopPropagation();
        setActionItem(data);
        try {
            // stays: worked out on the server from the stay's cancellation policy
            const refund = await getRefundAmount(
                typeShort === 'camp'
                    ? { type: 'camp', bookingId: data?._id }
                    : { amount: data?.amountPaid?.paid, startDate: data?.startDate }
            );
            setRefundAmount(refund);
        } catch (error) {
            setRefundAmount(
                typeShort === 'camp'
                    ? {
                          refAmount: 0,
                          canCancel: false,
                          reason: error?.response?.data?.message || 'Couldn’t check your refund. Please try again.'
                      }
                    : { refAmount: 0 }
            );
        }
        setShowModal(true);
    };

    const handleClick = (id) => {
        navigate(`/${typeMap[typeSel]}/details/${id}`);
    };

    const handleSubmitRating = async (e) => {
        e.preventDefault();

        const item = actionItem || detailsData;
        if (!popupRating) {
            toast.info('Please choose a rating');
            return;
        }
        const ratingData = {
            // the trip / activity / tour / stay that was booked (not the booking)
            packId: item?.[key]?._id || item?.id?._id,
            userName: localStorage.getItem('name') || '',
            userId: localStorage.getItem('userId') || '',
            rating: popupRating,
            organizerId: item?.organizerId?._id || item?.organizerId,
            comment
        };

        try {
            await userReview(typeShort, ratingData);
            toast.success('Thank you! Your rating has been submitted');
            setUserRating(popupRating);
            closeRatingModal();
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Couldn’t submit your rating.');
        }
    };

    const handleTypeChange = (type) => {
        setSearchParams({ type });
    };
    const key = idType[typeSel];

    return (
        <div>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div>
                <div>
                    <TitleBar title='My Bookings' />
                    <div className='customContainer my_booking'>
                        <div className='wishlistTabs'>
                            {types.map((type) => (
                                <button
                                    key={type}
                                    className={typeSel === type ? 'activeTab' : ''}
                                    onClick={() => handleTypeChange(type)}
                                >
                                    {type.charAt(0).toUpperCase() + type.slice(1)}
                                </button>
                            ))}
                        </div>
                        {bookingData?.length === 0 && !loading ? (
                            <EmptyPage />
                        ) : (
                            <div className='trips'>
                                <div className='tripStrapDiv'>
                                    {bookingData?.map((item, index) =>
                                        isMobile ? (
                                            <TripStrapMobile
                                                key={index}
                                                data={item}
                                                handleClick={handleClick}
                                                actions={bookingActions(item, typeMap[typeSel])}
                                                openForChange={openForChange}
                                                handleCancelBooking={handleCancelBooking}
                                                handleRescheduleBooking={handleRescheduleBooking}
                                                type={typeMap[typeSel]}
                                                openRatingModal={openRatingModal}
                                            />
                                        ) : (
                                            <TripStrapNew
                                                key={index}
                                                page='bookings'
                                                data={item}
                                                setDetailsData={setDetailsData}
                                                setRefetch={setRefetch}
                                                selectedId={selectedId}
                                                setSelectedId={setSelectedId}
                                            />
                                            //<TripStrap page='bookings' data={item} setRefetch={setRefetch} />
                                        )
                                    )}
                                </div>
                                {Object.keys(detailsData).length !== 0 && (
                                    <div
                                        className='tripBookingDetails'
                                        onClick={() => {
                                            handleClick(detailsData?.[key]?._id);
                                        }}
                                    >
                                        <div className='topContent'>
                                            <div className='imageDiv'>
                                                <img
                                                    src={
                                                        detailsData?.[key]?.cardImages?.length > 0
                                                            ? detailsData?.[key]?.cardImages[0]
                                                            : ''
                                                    }
                                                    alt='trvto_trip'
                                                    className='tripImageNew'
                                                    loading='lazy'
                                                    decoding='async'
                                                />
                                                <div className='imageDivTitle'>
                                                    <div>{detailsData?.[key]?.title}</div>
                                                    {/* trips: pickup point · stays: the unit booked */}
                                                    <div>{detailsData?.pickupLocation || detailsData?.unitName}</div>
                                                </div>
                                            </div>
                                            <div className='bookingHeader'>
                                                <div>
                                                    <p className='label'>Booking ID</p>
                                                    <p className='value'>{detailsData._id}</p>
                                                </div>

                                                <div
                                                    className={
                                                        detailsData.tripStatus === 'Cancelled' ? 'cancelled' : 'status'
                                                    }
                                                >
                                                    {detailsData.status}
                                                </div>
                                            </div>

                                            <hr />

                                            {/* Row 1 */}
                                            <div className='row'>
                                                <div className='col'>
                                                    <p className='label'>{getBookingDetailsByType(typeSel).label}</p>
                                                    <p className='value'>
                                                        {getFormattedDateReverse(detailsData.startDate)}
                                                    </p>
                                                </div>

                                                {typeSel === 'guide' ? (
                                                    <div className='col'>
                                                        <p className='label'>Place</p>
                                                        <p className='value'>{detailsData?.place?.name}</p>
                                                    </div>
                                                ) : (
                                                    <div className='col'>
                                                        <p className='label'>Duration</p>
                                                        <p className='value'>
                                                            {detailsData?.[key]?.quickView?.duration}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Row 2 */}
                                            <div className='row'>
                                                {typeSel === 'trip' ? (
                                                    <div className='col'>
                                                        <p className='label'>Start City</p>
                                                        <p className='value'>{detailsData?.startCity}</p>
                                                    </div>
                                                ) : (
                                                    detailsData?.meetupLocation?.name && (
                                                        <div className='col'>
                                                            <p className='label'>Meetup Location</p>
                                                            <a
                                                                className='value'
                                                                href={detailsData?.meetupLocation?.location}
                                                                target='_blank'
                                                                rel='noopener noreferrer'
                                                            >
                                                                {detailsData?.meetupLocation?.name}
                                                            </a>
                                                        </div>
                                                    )
                                                )}

                                                <div className='col'>
                                                    <p className='label'>No. of Seats</p>
                                                    <p className='value'>
                                                        <p className='value'>
                                                            {Array.isArray(detailsData?.totalSeats)
                                                                ? getTotalSeatCount(detailsData?.totalSeats)
                                                                : detailsData?.totalSeats || 0}
                                                        </p>
                                                    </p>
                                                </div>
                                            </div>
                                            <div className='row'>
                                                {typeSel === 'trip' ? (
                                                    <div className='col'>
                                                        <p className='label'>Pickup Location</p>
                                                        <p className='value'>{detailsData?.pickupLocation}</p>
                                                    </div>
                                                ) : typeSel === 'camp' ? (
                                                    <div className='col'>
                                                        <p className='label'>Check-out</p>
                                                        <p className='value'>
                                                            {getFormattedDateReverse(detailsData.endDate)}
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div className='col'>
                                                        <p className='label'>Time Slot</p>
                                                        <p className='value'>
                                                            {`${detailsData?.timeSlots?.startTime} - ${detailsData?.timeSlots?.endTime}`}
                                                        </p>
                                                    </div>
                                                )}
                                                <div className='col'>
                                                    <p className='label'>Organizer</p>
                                                    <p className='value'>{detailsData?.[key]?.organizerName}</p>
                                                </div>
                                            </div>
                                            <div className='amountDiv'>
                                                <span>Amount Paid </span>
                                                <span className='amountDivPrice'>
                                                    ₹{detailsData?.amountPaid?.paid}{' '}
                                                </span>
                                            </div>
                                            {detailsData?.amountPaid?.isReserved &&
                                                (typeSel === 'act' ? (
                                                    <div>
                                                        <span
                                                            style={{
                                                                color: 'red',
                                                                paddingLeft: '30px',
                                                                fontSize: '14px'
                                                            }}
                                                        >
                                                            Remaining - ₹
                                                            {(() => {
                                                                const total =
                                                                    Number(detailsData?.amountPaid?.total) || 0;

                                                                const reserve = Array.isArray(
                                                                    detailsData?.amountPaid?.reserve
                                                                )
                                                                    ? detailsData.amountPaid.reserve
                                                                    : [];

                                                                const totalSeats = Array.isArray(
                                                                    detailsData?.totalSeats
                                                                )
                                                                    ? detailsData.totalSeats
                                                                    : [];

                                                                const reservedAmount = reserve.reduce(
                                                                    (sum, reserveItem) => {
                                                                        const seat = totalSeats.find(
                                                                            (item) => item?.name === reserveItem?.name
                                                                        );

                                                                        const quantity = Number(seat?.qty) || 0;
                                                                        const amount = Number(reserveItem?.amount) || 0;

                                                                        return sum + amount * quantity;
                                                                    },
                                                                    0
                                                                );

                                                                return Math.max(0, total - reservedAmount);
                                                            })()}{' '}
                                                            (to be paid on activity)
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <span
                                                            style={{
                                                                color: 'red',
                                                                paddingLeft: '30px',
                                                                fontSize: '14px'
                                                            }}
                                                        >
                                                            Remaining - ₹
                                                            {/* stays send the balance worked out on the server */}
                                                            {detailsData?.amountPaid?.balance ??
                                                                Math.max(
                                                                    0,
                                                                    (Number(detailsData?.amountPaid?.total) || 0) -
                                                                        (Number(detailsData?.amountPaid?.reserve) || 0) *
                                                                            (Number(detailsData?.totalSeats) || 0)
                                                                )}{' '}
                                                            (to be paid on trip)
                                                        </span>
                                                    </div>
                                                ))}
                                        </div>
                                        <div className='buttonsBo'>
                                            {can.pickup && (
                                                <div
                                                    onClick={(event) =>
                                                        openForChange(event, detailsData, { isTripMoreThanOneDays: true })
                                                    }
                                                    className='button1'
                                                >
                                                    Update Pickup Location
                                                </div>
                                            )}
                                            {can.meetup && (
                                                <div
                                                    onClick={(event) => openForChange(event, detailsData)}
                                                    className='button1'
                                                >
                                                    Change Meetup Location
                                                </div>
                                            )}
                                            {can.reschedule && (
                                                <button className='button1' onClick={(e) => handleRescheduleBooking(e)}>
                                                    {typeShort === 'camp' ? 'Change Dates' : 'Reschedule'}
                                                </button>
                                            )}
                                            {can.rate && (
                                                <div onClick={(e) => openRatingModal(e)} className='button1'>
                                                    Rate Now
                                                </div>
                                            )}
                                            {can.cancel && (
                                                <div
                                                    className='button2'
                                                    onClick={(e) => {
                                                        handleCancelBooking(e, detailsData);
                                                    }}
                                                    style={{ color: 'black' }}
                                                >
                                                    Cancel
                                                </div>
                                            )}
                                            {can.bookAgain && (
                                                <div
                                                    className='button1'
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleClick(detailsData?.id?._id);
                                                    }}
                                                >
                                                    Book Again
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className='myBookingPagination'>
                            <Pagination
                                paginationClick={paginationClick}
                                numberOfRows={totalData}
                                offSet={offSet}
                                pageNumber={pageNumber}
                                limit={limit}
                            />
                        </div>
                    </div>
                </div>
                {showRatingModal && (
                    <div className='ratingModal'>
                        <div className='modalContent'>
                            <div>
                                <h3>{RATE_TITLE[typeShort] || RATE_TITLE.trip}</h3>
                            </div>
                            <RateTrip
                                props={{
                                    popupRating,
                                    setPopupRating,
                                    comment,
                                    setComment
                                }}
                            />
                            <div className='modalActions'>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        closeRatingModal(e);
                                    }}
                                    className='closeModal'
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleSubmitRating(e);
                                    }}
                                    className='submitRating'
                                >
                                    Submit
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {showRescheduleModal &&
                    (typeShort === 'camp' ? (
                        <StayRescheduleModal
                            data={actionItem || detailsData}
                            onClose={() => setShowRescheduleModal(false)}
                            onChanged={() => setRefetch((prev) => !prev)}
                        />
                    ) : (
                        <RescheduleBooking
                            setShowRescheduleModal={setShowRescheduleModal}
                            data={actionItem || detailsData}
                            type={getTypeShort(typeSel)}
                        />
                    ))}
                {showModal && (
                    <CancelBooking
                        setShowModal={setShowModal}
                        message='Do you want to cancel this booking?'
                        refundAmount={refundAmount?.refAmount ?? null}
                        note={
                            refundAmount?.canCancel === false
                                ? refundAmount?.reason
                                : refundAmount?.breakdown?.rule
                                  ? `${refundAmount.breakdown.rule}${refundAmount.refAmount ? ', less 2% payment charges' : ''}`
                                  : null
                        }
                        confirmDisabled={refundAmount?.canCancel === false}
                        handleConfirm={(e) => {
                            e.stopPropagation();
                            handleBooking(actionItem || detailsData, setShowModal, typeSel)
                                .then(() => {
                                    setRefetch((prev) => !prev);
                                })
                                .catch((err) => console.error('Cancel failed', err));
                        }}
                    />
                )}
            </div>
        </div>
    );
}
